import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Send, Mail, MessageSquare, Loader2, AlertTriangle,
  RefreshCw,
} from "lucide-react";

/**
 * AiComposeDialog — universal Email + SMS compose modal with AI assistance.
 *
 * Behavior:
 *   1. On open, POSTs to /api/leads/{leadId}/ai-compose with an empty user_note
 *      → AI writes a fresh, category-aware draft (probate, tax delinquent, etc.)
 *   2. User can edit the draft OR type their own rough note.
 *   3. Clicking "Rewrite with AI" re-POSTs with the current body as user_note
 *      → AI polishes into professional copy while keeping the user's intent.
 *   4. Send:
 *        - email → POST /api/emails/send
 *        - sms   → disabled banner (A2P 10DLC approval pending)
 *
 * Props:
 *   open, onOpenChange   controlled Dialog state
 *   channel              "email" | "sms"
 *   lead                 { id, name, email, phone, ... }
 */
export default function AiComposeDialog({ open, onOpenChange, channel, lead }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState({ key: null, label: null });
  const [loadingAi, setLoadingAi] = useState(false);
  const [sending, setSending] = useState(false);

  const isSms = channel === "sms";
  const Icon = isSms ? MessageSquare : Mail;

  const fetchDraft = async (userNote = "") => {
    if (!lead?.id) return;
    setLoadingAi(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/ai-compose`, {
        channel,
        user_note: userNote,
      });
      if (data.subject) setSubject(data.subject);
      setBody(data.body || "");
      setCategory({ key: data.category, label: data.category_label });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI compose failed");
    } finally { setLoadingAi(false); }
  };

  // Auto-draft on open
  useEffect(() => {
    if (open && lead?.id) {
      setSubject("");
      setBody("");
      setCategory({ key: null, label: null });
      fetchDraft("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.id, channel]);

  const rewrite = () => {
    if (!body.trim()) return toast.info("Type a rough note first, then I'll polish it");
    fetchDraft(body);
  };

  const sendEmail = async () => {
    if (!lead?.email) return toast.error("This lead has no email address");
    if (!subject.trim() || !body.trim())
      return toast.error("Subject and body required");
    setSending(true);
    try {
      // Convert plain-text body to minimal HTML (preserve paragraph breaks)
      const html = body
        .split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      await api.post("/emails/send", {
        recipient_email: lead.email,
        subject,
        html_body: html,
        lead_id: lead.id,
      });
      toast.success("Email sent");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const charCount = body.length;
  const smsWarn = isSms && charCount > 290;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-2xl" data-testid="ai-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Icon size={18} className={isSms ? "text-blue-600" : "text-emerald-600"} />
            {isSms ? "Text" : "Email"} {lead?.name || "this lead"}
            <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 flex items-center gap-1">
              <Sparkles size={10} /> AI-drafted
            </span>
          </DialogTitle>
          <DialogDescription>
            {isSms
              ? "AI drafts a category-aware SMS from your lead's motivation signals. Edit it, or type your own rough note and hit 'Rewrite with AI' to polish it."
              : "AI drafts a category-aware email from your lead's motivation signals. Edit it, or type your own rough note and hit 'Rewrite with AI' to polish it."}
          </DialogDescription>
        </DialogHeader>

        {/* Category badge */}
        {category.label && (
          <div className="flex items-center gap-2 text-xs" data-testid="compose-category-badge">
            <span className="text-muted-foreground">Detected category:</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-medium">
              {category.label}
            </span>
          </div>
        )}

        {/* SMS A2P blocker banner */}
        {isSms && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-start gap-2"
            data-testid="a2p-warning"
          >
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <b>SMS sending is paused</b> — waiting on Twilio A2P 10DLC brand
              registration approval. You can draft &amp; refine the message
              here; sending will unlock once the brand is approved.
            </div>
          </div>
        )}

        {/* Recipient */}
        <div className="text-[11px] text-muted-foreground">
          To: <span className="font-mono text-foreground">
            {isSms
              ? (lead?.phone || "— no phone on file")
              : (lead?.email || "— no email on file")}
          </span>
        </div>

        {/* Subject (email only) */}
        {!isSms && (
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="AI will fill this…"
              className="mt-1"
              data-testid="compose-subject-input"
            />
          </div>
        )}

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[10px] uppercase tracking-widest">
              Message
            </Label>
            <span className={`text-[10px] ${smsWarn ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>
              {isSms ? `${charCount} / 320 chars` : `${charCount} chars`}
            </span>
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={loadingAi
              ? "AI is drafting…"
              : "Type your rough note — AI will polish it when you click Rewrite."}
            className={`min-h-[180px] text-sm ${isSms ? "font-sans" : ""}`}
            disabled={loadingAi}
            data-testid="compose-body-textarea"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={rewrite}
            disabled={loadingAi}
            className="rounded-full"
            data-testid="compose-rewrite-btn"
          >
            {loadingAi
              ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Rewriting…</>
              : <><RefreshCw size={13} className="mr-1.5" /> Rewrite with AI</>}
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="compose-cancel-btn"
          >
            Cancel
          </Button>
          {isSms ? (
            <Button
              disabled
              title="SMS sending pending A2P 10DLC approval"
              className="bg-slate-300 text-slate-600 cursor-not-allowed rounded-full"
              data-testid="compose-send-sms-btn"
            >
              <Send size={13} className="mr-1.5" /> Send (locked)
            </Button>
          ) : (
            <Button
              onClick={sendEmail}
              disabled={sending || loadingAi || !subject.trim() || !body.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
              data-testid="compose-send-email-btn"
            >
              {sending
                ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Sending…</>
                : <><Send size={13} className="mr-1.5" /> Send email</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
